import * as React from 'react';
import ReactHotToast from 'react-hot-toast';

import { useAskNeedlDispatchContext } from '@/features/ChatBotV2/context';
import type { AttachmentType } from '@/features/ChatBotV2/types';
import { useInitUploadComplete } from '@/hooks/query/useInitUploadComplete';
import { getMimeTypeFromFileType } from '@/utils/fileAndMimeTypes';

import { ASK_NEEDL_ACTIONS } from './useAskNeedlState';

export const useUploadAndProcessAttachments = () => {
  const dispatch = useAskNeedlDispatchContext();
  const { initUploadComplete } = useInitUploadComplete();

  const uploadAndProcessAttachments = React.useCallback(
    (files: File[], feedId?: string) => {
      const attachments = files.map((file) => ({
        id: crypto.randomUUID(),
        name: file.name,
        mime_type: getMimeTypeFromFileType(file.type),
        file: file,
        file_type: file.type,
        status: 'UPLOADING',
      })) as AttachmentType[];

      dispatch({
        type: ASK_NEEDL_ACTIONS.SET_SESSION_ON_FILES_DROP,
        payload: { attachments },
      });

      initUploadComplete(
        {
          documents: attachments.map((attachment) => ({
            ...attachment,
            mime_type: attachment.file_type,
          })),
          source: 'ask_needl',
          ...(feedId ? { feed_id: feedId } : {}),
        },
        {
          onInitSuccess: (response) => {
            const uploadConfigs = response.upload_configs || [];
            const attachmentBucketKeyMap = uploadConfigs.map((config) => ({
              id: config.id,
              bucket: config.metadata_fields.bucket,
              key: config.metadata_fields.key,
            }));

            attachmentBucketKeyMap.forEach(({ id, bucket, key }) => {
              dispatch({
                type: ASK_NEEDL_ACTIONS.ADD_ATTACHMENT_BUCKET_KEY,
                payload: {
                  attachmentId: id,
                  bucket,
                  key,
                },
              });
            });
          },

          onInitError: (error) => {
            ReactHotToast.error('File(s) upload failed. Please try again.');
            dispatch({
              type: ASK_NEEDL_ACTIONS.SET_ATTACHMENTS_STATUS,
              payload: {
                attachmentIds: attachments.map((attachment) => attachment.id),
                status: 'UPLOADING_ERROR',
                error: 'Error getting upload configs',
              },
            });
            console.error('Error getting upload configs:', error);
          },
          onUploadSuccess: () => {
            dispatch({
              type: ASK_NEEDL_ACTIONS.SET_ATTACHMENTS_STATUS,
              payload: {
                attachmentIds: attachments.map((attachment) => attachment.id),
                status: 'UPLOADED',
              },
            });
          },
          onUploadError: (error) => {
            ReactHotToast.error('File(s) upload failed. Please try again.');
            dispatch({
              type: ASK_NEEDL_ACTIONS.SET_ATTACHMENTS_STATUS,
              payload: {
                attachmentIds: attachments.map((attachment) => attachment.id),
                status: 'UPLOADING_ERROR',
                error: 'Error uploading files',
              },
            });
            console.error('Error uploading files:', error);
          },
          onUploadCompleteSuccess: (response) => {
            dispatch({
              type: ASK_NEEDL_ACTIONS.SET_ATTACHMENTS_STATUS,
              payload: {
                attachmentIds: attachments.map((attachment) => attachment.id),
                status: 'PROCESSING',
              },
            });

            if (response.feed_id) {
              dispatch({
                type: ASK_NEEDL_ACTIONS.SET_ATTACHMENTS_FEED_ID,
                payload: {
                  feedId: response.feed_id,
                },
              });
            }
          },
          onUploadProgress: (progress, document) => {
            dispatch({
              type: ASK_NEEDL_ACTIONS.UPDATE_ATTACHMENT_PROGRESS,
              payload: {
                attachmentId: document.id,
                progress,
              },
            });
          },
        }
      );
    },
    [dispatch, initUploadComplete]
  );

  return {
    uploadAndProcessAttachments,
  };
};
